import { Component, effect, inject, input, OnInit, output, signal } from '@angular/core';

import { SpinnerService } from '@hglobal/common';

import { AuthStore, VerifyEmailPhoneReq } from '@pp/core/auth-store';
import { Account, ApiError, Team } from '@pp/core/entities';
import { GlobalToastService } from '@pp/core/services';

import { EmailPhoneVerificationComponent } from './email-phone-verification/email-phone-verification.component';
import { UserProfileFormComponent } from './form/form.component';
import { EditUserProfileReq } from './personal-info.entities';

@Component({
  selector: 'pp-personal-info',
  imports: [EmailPhoneVerificationComponent, UserProfileFormComponent],
  templateUrl: './personal-info.component.html',
  styleUrl: './personal-info.component.scss',
})
export class PersonalInfoComponent implements OnInit {
  readonly #authStore = inject(AuthStore);
  readonly #spinner = inject(SpinnerService);
  readonly #globalToastService = inject(GlobalToastService);

  dialogData = input<{ user: Account; org: Team }>();
  readonlyRights = input<boolean>();
  unsavedChanges = output<boolean>();
  closeDialog = output<boolean>();

  user = signal<Account>(null);
  changedUser = signal<Account>(null);
  org = signal<Team>(null);
  isSecondStep = signal(false);
  isEmailChanged = signal(false);

  changeEmailPhoneSuccessEffect = effect(() => {
    const success = this.#authStore.isChangeEmailPhoneSuccess();
    if (success) {
      this.#onChangeSuccess();
    }
  });

  changeEmailPhoneFailedEffect = effect(() => {
    const error = this.#authStore.changeEmailPhoneError();
    if (error) {
      this.#onChangeError(error);
    }
  });

  verifyEmailPhoneSuccessEffect = effect(() => {
    const success = this.#authStore.isVerifyEmailPhoneSuccess();
    if (success) {
      this.#onVerifySuccess();
    }
  });

  verifyEmailPhoneFailedEffect = effect(() => {
    const error = this.#authStore.verifyEmailPhoneError();
    if (error) {
      this.#onVerifyError(error);
    }
  });

  ngOnInit() {
    this.user.set(this.dialogData().user);
    this.org.set(this.dialogData().org);

    if (this.readonlyRights()) return;
  }

  handleUnsavedChanges(unsaved: boolean): void {
    if (!unsaved) {
      this.changedUser.set(null);
    }
    this.unsavedChanges.emit(unsaved);
  }

  changeProfile(request: EditUserProfileReq): void {
    if (this.readonlyRights()) return;

    if (request.email === this.user().email && request.phone === this.user().phone) {
      this.closeDialog.emit(false);

      return;
    }
    this.isEmailChanged.set(!!request.email);
    this.changedUser.set({ ...this.dialogData().user, ...request });

    this.#authStore.changeEmailPhone(request);
  }

  verifyEmailPhone(request: VerifyEmailPhoneReq): void {
    if (this.readonlyRights()) return;

    this.#authStore.verifyEmailPhone(request);
  }

  resendEmailCode(): void {
    if (this.readonlyRights()) return;

    this.#authStore.resendEmailVerificationCode();
  }

  resendPhoneCode(): void {
    if (this.readonlyRights()) return;

    this.#authStore.resendPhone();
  }

  #onChangeSuccess(): void {
    this.isSecondStep.set(true);
    this.#authStore.resetRequestStatus('changeEmailPhone');
  }

  #onChangeError(error: ApiError): [] {
    const message = error.originalError?.error?.errorDescription || error.originalError?.error?.error_description || error.message;
    this.#globalToastService.showError(message);

    return [];
  }

  #onVerifySuccess(): void {
    const successKey = this.isEmailChanged() ? 'editUser.notification.success.email' : 'editUser.notification.success.phone';
    this.#globalToastService.showSuccess(successKey);
    this.#authStore.resetRequestStatus('verifyEmailPhone');
    this.closeDialog.emit(true);
  }

  #onVerifyError(error: ApiError): [] {
    const message = error.originalError?.error?.errorDescription || error.originalError?.error?.error_description || error.message;
    this.#globalToastService.showError(message);

    return [];
  }
}
